import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Calculator, Copy, Printer, ShieldCheck, Truck, Wallet, FileCheck2, BadgeCheck, TrendingDown } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { QUANTITY_UNITS, perUnit } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const r2 = (n) => Math.round(n * 100) / 100;
const GST_RATES = [0, 5, 12, 18];
const feePct = (p) => p?.design_take_rate_pct ?? p?.platform_take_rate_pct ?? 0;
const card = { background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, padding: "18px 20px" };

export function computeBid(base, pct, rate) {
  const b = r2(Number(base) || 0);
  const fee = r2(b * pct / 100);
  const feeGst = r2(fee * 0.18);
  const gst = r2(b * rate / 100);
  const tds = r2(b * 0.01);
  return { base: b, fee, feeGst, gst, tds, invoice: r2(b + gst), payout: r2(b + gst - fee - feeGst - tds), recoverable: r2(feeGst + tds), keep: r2(b - fee) };
}

function usePlans() {
  const { user } = useAuth();
  const [state, setState] = useState({ plans: [], current: null });
  useEffect(() => {
    if (!user?.id) return;
    Promise.all([
      axios.get(`${API}/subscriptions/plans`, { params: { role: user.role } }),
      axios.get(`${API}/users/${user.id}/subscription`),
    ]).then(([p, s]) => setState({ plans: p.data[user.role] || [], current: s.data?.plan || null }))
      .catch(() => setState({ plans: [], current: null }));
  }, [user?.id, user?.role]);
  return { ...state, role: user?.role };
}

function Field({ label, children }) {
  return <label style={{ display: "grid", gap: 5, fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: ".04em" }}>{label}{children}</label>;
}
const ctl = { border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontSize: 14, fontFamily: "inherit", background: "#fff", color: "#0F172A", width: "100%", minWidth: 0, boxSizing: "border-box" };

function Line({ label, value, tone, bold, testid, hint }) {
  const color = tone === "minus" ? "#B91C1C" : tone === "plus" ? "#047857" : "#0F172A";
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "7px 0", borderBottom: "1px dashed #E2E8F0", fontSize: 13.5, fontWeight: bold ? 700 : 400 }}>
      <span>{label}{hint && <small style={{ display: "block", fontSize: 11, color: "#94a3b8", fontWeight: 400 }}>{hint}</small>}</span>
      <span data-testid={testid} style={{ color, whiteSpace: "nowrap" }}>{tone === "minus" ? "− " : tone === "plus" ? "+ " : ""}{value}</span>
    </div>
  );
}

function Breakdown({ c, rate, pct, word, qty, unit }) {
  const pu = perUnit(c.base, qty, unit);
  return (
    <div data-testid="calc-breakdown">
      <Line label={`Gross ${word} (your invoice taxable value)`} value={inr(c.base)} testid="calc-base" hint={pu ? `${pu} excl. GST` : null}/>
      <Line label={`Product GST @ ${rate}% (paid by buyer)`} value={inr(c.gst)} tone="plus" testid="calc-gst"/>
      <Line label="Your invoice to GoPrintPack™" value={inr(c.invoice)} bold testid="calc-invoice"/>
      <Line label={`Platform fee @ ${pct}%`} value={inr(c.fee)} tone="minus" testid="calc-fee"/>
      <Line label="18% GST on platform fee" value={inr(c.feeGst)} tone="minus" testid="calc-fee-gst" hint="Separate tax invoice; claim it as ITC"/>
      <Line label="TDS @ 1% u/s 194-O" value={inr(c.tds)} tone="minus" testid="calc-tds" hint="Credited to your Form 26AS"/>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, padding: "12px 14px", borderRadius: 10, background: "#ECFDF5", border: "1px solid #A7F3D0" }}>
        <b style={{ color: "#065F46" }}>Net payout to your bank</b>
        <b data-testid="calc-payout" style={{ fontSize: 22, color: "#065F46" }}>{inr(c.payout)}</b>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginTop: 12 }}>
        <Mini label="You keep (ex-GST)" value={inr(c.keep)} testid="calc-keep"/>
        <Mini label="Recoverable later" value={inr(c.recoverable)} testid="calc-recoverable" hint="Fee GST (ITC) + TDS credit"/>
        <Mini label="Effective fee" value={`${c.base ? r2(c.fee / c.base * 100) : 0}%`} testid="calc-effective"/>
      </div>
    </div>
  );
}

const Mini = ({ label, value, hint, testid }) => (
  <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: "9px 11px" }}>
    <div style={{ fontSize: 10.5, color: "#64748b", textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</div>
    <b data-testid={testid} style={{ fontSize: 15, color: "#0F172A" }}>{value}</b>
    {hint && <div style={{ fontSize: 10.5, color: "#94a3b8" }}>{hint}</div>}
  </div>
);

function breakdownText(c, rate, pct, word, planName) {
  return [
    `GoPrintPack™ bid breakdown (${planName || "plan"} · ${pct}% platform fee)`,
    `Gross ${word}: ${inr(c.base)}`, `Product GST @${rate}%: + ${inr(c.gst)}`, `Invoice to GoPrintPack™: ${inr(c.invoice)}`,
    `Platform fee @${pct}%: − ${inr(c.fee)}`, `18% GST on fee (ITC): − ${inr(c.feeGst)}`, `TDS 1% u/s 194-O: − ${inr(c.tds)}`,
    `Net payout: ${inr(c.payout)}`, `Recoverable later (ITC + TDS): ${inr(c.recoverable)}`,
  ].join("\n");
}

function printBreakdown(text) {
  const w = window.open("", "_blank", "width=640,height=720");
  if (!w) { toast.error("Allow pop-ups to print"); return; }
  w.document.write(`<html><head><title>Bid breakdown</title></head><body style="font-family:Arial,sans-serif;padding:28px"><pre style="font-size:14px;line-height:1.8">${text.replace(/</g, "&lt;")}</pre><p style="font-size:11px;color:#666">Estimate only. Final amounts are on your GoPrintPack™ invoices.</p></body></html>`);
  w.document.close(); w.focus(); w.print();
}

function CalculatorWidget({ plans, current, role }) {
  const word = role === "printer" ? "bid" : "quote";
  const [mode, setMode] = useState("bid");
  const [amount, setAmount] = useState("10870");
  const [rate, setRate] = useState(role === "finishing_partner" ? 5 : 18);
  const [qty, setQty] = useState("1000");
  const [unit, setUnit] = useState("units");
  const [planId, setPlanId] = useState("");
  const plan = plans.find(p => p.id === planId) || plans.find(p => p.id === current?.id) || current;
  const pct = feePct(plan);
  const base = mode === "bid" ? Number(amount) || 0 : (pct < 100 ? Math.ceil((Number(amount) || 0) / (1 - pct / 100)) : 0);
  const c = useMemo(() => computeBid(base, pct, rate), [base, pct, rate]);
  const copy = () => navigator.clipboard.writeText(breakdownText(c, rate, pct, word, plan?.name)).then(() => toast.success("Breakdown copied"));

  return (
    <section data-testid="bid-calculator" style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <Calculator size={18} style={{ color: "#1d4ed8" }}/><h3 style={{ margin: 0, fontSize: 17 }}>Interactive {role === "printer" ? "Bid" : "Quote"} Calculator</h3>
      </div>
      <div role="tablist" style={{ display: "inline-flex", flexWrap: "wrap", maxWidth: "100%", background: "#F1F5F9", borderRadius: 18, padding: 3, marginBottom: 14 }}>
        {[["bid", `I ${word} ₹X → what do I get?`], ["earn", `I want to earn ₹X → what should I ${word}?`]].map(([m, l]) => (
          <button key={m} data-testid={`calc-mode-${m}`} onClick={() => setMode(m)} style={{ border: 0, borderRadius: 999, padding: "7px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", background: mode === m ? "#1E3A8A" : "transparent", color: mode === m ? "#fff" : "#334155", transition: "background-color .2s" }}>{l}</button>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 16 }}>
        <Field label={mode === "bid" ? `Gross ${word} (₹, pre-GST)` : "Target earnings (₹, after fee, pre-GST)"}>
          <input data-testid="calc-amount" type="number" min="0" step="any" value={amount} onChange={e => setAmount(e.target.value)} style={ctl}/>
        </Field>
        <Field label="Product GST">
          <select data-testid="calc-gst-rate" value={rate} onChange={e => setRate(Number(e.target.value))} style={ctl}>
            {GST_RATES.map(g => <option key={g} value={g}>{g}%</option>)}
          </select>
        </Field>
        <Field label="Quantity">
          <input data-testid="calc-qty" type="number" min="0" step="any" value={qty} onChange={e => setQty(e.target.value)} style={ctl}/>
        </Field>
        <Field label="Unit">
          <select data-testid="calc-unit" value={unit} onChange={e => setUnit(e.target.value)} style={ctl}>
            {QUANTITY_UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Plan">
          <select data-testid="calc-plan" value={plan?.id || ""} onChange={e => setPlanId(e.target.value)} style={ctl}>
            {plans.map(p => <option key={p.id} value={p.id}>{p.name} · {feePct(p)}%{p.id === current?.id ? " (yours)" : ""}</option>)}
          </select>
        </Field>
      </div>
      {mode === "earn" && base > 0 && (
        <div data-testid="calc-suggested" style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13.5 }}>
          Submit a Gross {word} of <b style={{ fontSize: 16 }}>{inr(base)}</b> to earn {inr(c.keep)} after the {pct}% platform fee (before GST).
        </div>
      )}
      <Breakdown c={c} rate={rate} pct={pct} word={word} qty={qty} unit={unit}/>
      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button data-testid="calc-copy" className="secondary-btn" onClick={copy}><Copy size={14}/> Copy breakdown</button>
        <button data-testid="calc-print" className="secondary-btn" onClick={() => printBreakdown(breakdownText(c, rate, pct, word, plan?.name))}><Printer size={14}/> Print</button>
      </div>
      <UpgradeCompare plans={plans} current={current} base={c.base} rate={rate}/>
    </section>
  );
}

function UpgradeCompare({ plans, current, base, rate }) {
  if (!plans.length || !(base > 0)) return null;
  const mine = computeBid(base, feePct(current), rate);
  return (
    <div data-testid="calc-upgrade" style={{ marginTop: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: 14, marginBottom: 8 }}><TrendingDown size={16} style={{ color: "#047857" }}/> Upgrade and save on this order</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        {plans.map(p => {
          const c = computeBid(base, feePct(p), rate);
          const save = r2(c.payout - mine.payout);
          const diff = feePct(current) - feePct(p);
          const breakEven = diff > 0 && p.price_inr ? Math.ceil(p.price_inr / (diff / 100)) : null;
          const isMine = p.id === current?.id;
          return (
            <div key={p.id} data-testid={`calc-plan-card-${p.id}`} style={{ border: `1px solid ${isMine ? "#1E3A8A" : "#E2E8F0"}`, borderRadius: 10, padding: "10px 12px", background: isMine ? "#EFF6FF" : "#fff" }}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>{p.name}{isMine && <span style={{ color: "#1E3A8A" }}> · your plan</span>}</div>
              <div style={{ fontSize: 11, color: "#64748b" }}>{feePct(p)}% fee · {p.price_inr ? `${inr(p.price_inr)}/mo + GST` : "Free"}</div>
              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 6 }}>{inr(c.payout)}</div>
              {save > 0 && <div data-testid={`calc-save-${p.id}`} style={{ fontSize: 11.5, color: "#047857", fontWeight: 600 }}>Save {inr(save)} on this order</div>}
              {breakEven && <div style={{ fontSize: 10.5, color: "#64748b" }}>Pays for itself above {inr(breakEven)} of orders a month</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FeeGuide({ plans, current, role }) {
  const word = role === "printer" ? "bid" : "quote";
  const ex = computeBid(10870, 8, 12);
  return (
    <section data-testid="fee-guide" style={card}>
      <h3 style={{ margin: "0 0 4px", fontSize: 17 }}>Fee Guide</h3>
      <p style={{ margin: "0 0 14px", fontSize: 13, color: "#475569" }}>GoPrintPack™ is the Merchant of Record: buyers see only your Gross {word} (plus GST) and never your identity or our fee. Always quote your final pre-GST price: production cost + your margin + our platform fee.</p>
      <div style={{ overflowX: "auto", marginBottom: 16 }}>
      <table data-testid="fee-guide-table" style={{ width: "100%", minWidth: 560, borderCollapse: "collapse", fontSize: 13 }}>
        <thead><tr style={{ background: "#F1F5F9", textAlign: "left" }}>
          {["Plan", "Platform fee", "GST on fee", "Subscription", "Payout release"].map(h => <th key={h} style={{ padding: "8px 10px" }}>{h}</th>)}
        </tr></thead>
        <tbody>
          {plans.map(p => (
            <tr key={p.id} style={{ borderBottom: "1px solid #E2E8F0", background: p.id === current?.id ? "#EFF6FF" : undefined }}>
              <td style={{ padding: "8px 10px", fontWeight: 600 }}>{p.name}{p.id === current?.id && " (yours)"}</td>
              <td style={{ padding: "8px 10px" }}>{feePct(p)}% of Gross {word}</td>
              <td style={{ padding: "8px 10px" }}>18% of the fee</td>
              <td style={{ padding: "8px 10px" }}>{p.price_inr ? `${inr(p.price_inr)}/mo + 18% GST` : "Free"}</td>
              <td style={{ padding: "8px 10px" }}>{p.escrow_speed_label || (p.escrow_release_hours ? `${p.escrow_release_hours}h after delivery` : role === "agency" ? "On buyer's final-file approval" : "Standard")}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
        <div style={{ background: "#F8FAFC", borderRadius: 10, padding: "12px 14px", fontSize: 13, lineHeight: 1.65 }}>
          <b>Worked example</b> (8% fee, 12% GST)<br/>
          Gross {word} {inr(ex.base)} + GST {inr(ex.gst)} = invoice {inr(ex.invoice)}<br/>
          − fee {inr(ex.fee)} − fee GST {inr(ex.feeGst)} − TDS {inr(ex.tds)}<br/>
          = <b>net payout {inr(ex.payout)}</b>
        </div>
        <div style={{ background: "#F8FAFC", borderRadius: 10, padding: "12px 14px", fontSize: 13, lineHeight: 1.65 }}>
          <b>GST, ITC & TDS for you</b><br/>
          • You invoice us for the full Gross {word} + product GST, and pay that GST in your GSTR-3B.<br/>
          • Our fee GST comes on a separate tax invoice (SAC 998599). Claim it as ITC.<br/>
          • 1% TDS u/s 194-O shows in your Form 26AS. Claim it against your income tax.
        </div>
      </div>
    </section>
  );
}

function PayoutTimeline({ current }) {
  const steps = [
    [ShieldCheck, "Award & escrow", "Buyer funds the full amount into Razorpay escrow before production starts."],
    [Truck, "Produce & dispatch", "Ship with a blind label. Buyer and supplier identities stay masked."],
    [BadgeCheck, "Delivery confirmed", "Buyer confirms delivery, or it auto-confirms after tracking shows delivered."],
    [Wallet, "Payout released", `Released ${current?.escrow_release_hours ? `${current.escrow_release_hours}h` : "per your plan"} after delivery, net of fee, fee GST and TDS.`],
    [FileCheck2, "Invoices & 26AS", "Supplier invoice, fee invoice and settlement sheet appear in Invoices. TDS shows in 26AS quarterly."],
  ];
  return (
    <section data-testid="payout-timeline" style={card}>
      <h3 style={{ margin: "0 0 12px", fontSize: 17 }}>Payout timeline</h3>
      <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 12 }}>
        {steps.map(([Icon, t, d], i) => (
          <li key={t} data-testid={`timeline-step-${i}`} style={{ display: "flex", gap: 12 }}>
            <span style={{ width: 30, height: 30, borderRadius: 999, background: "#EFF6FF", color: "#1d4ed8", display: "grid", placeItems: "center", flexShrink: 0 }}><Icon size={15}/></span>
            <div style={{ fontSize: 13 }}><b>{i + 1}. {t}</b><div style={{ color: "#64748b" }}>{d}</div></div>
          </li>
        ))}
      </ol>
    </section>
  );
}

const FAQS = [
  ["Does the buyer see my platform fee?", "No. Buyers see only your Gross bid/quote plus GST (and delivery for print jobs). Your identity and our fee stay hidden."],
  ["Is delivery part of my bid or quote?", "No. Shiprocket delivery is charged to the buyer separately at checkout. If you ship with your own courier, that charge is added on top of your bid."],
  ["Why is GST charged on the platform fee?", "The platform fee is a service we supply to you, so it attracts 18% GST. You get a tax invoice and can claim the full GST as input tax credit."],
  ["What if I'm not GST-registered?", "You can't claim the fee GST as ITC, so it becomes a cost. The calculator's 'Recoverable later' figure assumes you are registered."],
  ["How do I lower my fee?", "Upgrade your plan in Plans & Billing. The comparison above shows how much you'd save on this order."],
  ["What if the buyer picked the wrong GST class?", "Confirm or correct the GST class in your bid. Admin reviews it, and any later correction is handled through a credit or debit note."],
];

function Faq() {
  return (
    <section data-testid="fee-faq" style={card}>
      <h3 style={{ margin: "0 0 10px", fontSize: 17 }}>FAQ</h3>
      {FAQS.map(([q, a], i) => (
        <details key={q} data-testid={`faq-${i}`} style={{ borderBottom: "1px solid #E2E8F0", padding: "9px 0" }}>
          <summary style={{ cursor: "pointer", fontWeight: 600, fontSize: 13.5 }}>{q}</summary>
          <p style={{ margin: "6px 0 0", fontSize: 13, color: "#475569" }}>{a}</p>
        </details>
      ))}
    </section>
  );
}

export default function BidCalculatorPage() {
  const { plans, current, role } = usePlans();
  return (
    <section className="content" data-testid="bid-calculator-page" style={{ display: "grid", gap: 18 }}>
      <div className="section-heading" style={{ display: "block" }}>
        <p className="eyebrow">Pricing tools</p>
        <h2>Keep more of every order</h2>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>Work out the right Gross {role === "printer" ? "bid" : "quote"}, see exactly what lands in your bank, and how GST, ITC and TDS work on GoPrintPack™.</p>
      </div>
      {!current ? <div style={{ ...card, fontSize: 13, color: "#64748b" }}>Loading your plan…</div> : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr)", gap: 18, alignItems: "start" }} className="bid-calc-grid">
          <div style={{ display: "grid", gap: 18 }}>
            <CalculatorWidget plans={plans} current={current} role={role}/>
            <FeeGuide plans={plans} current={current} role={role}/>
          </div>
          <div style={{ display: "grid", gap: 18 }}>
            <PayoutTimeline current={current}/>
            <Faq/>
          </div>
        </div>
      )}
    </section>
  );
}
